import React, { useState } from 'react';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { AlertCircle, Loader2, Mail, Globe, Shield } from 'lucide-react';
import { auth } from '../firebase';
import {
  signInWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  sendSignInLinkToEmail,
} from 'firebase/auth';
import { useAppSettings } from '../hooks/useAppSettings'; // Import the new hook

export function CapacitorLoginScreen() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);

  const config = useAppSettings();
  const baseUrl =
    import.meta.env.VITE_APP_BASE_URL || 'https://kowope.akandie.com';
  const displayUrl = baseUrl.replace(/^https?:\/\//, '');
  const packageName =
    import.meta.env.VITE_PACKAGE_NAME || 'com.akandiecomputers.kowope';

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Sign in on the native layer
      const result = await FirebaseAuthentication.signInWithGoogle();

      if (!result.credential?.idToken) {
        throw new Error(
          'Google Sign-In was cancelled or failed to return an ID token.'
        );
      }

      // 2. Create web credentials from the native result
      const credential = GoogleAuthProvider.credential(
        result.credential.idToken
      );

      // 3. Sign in on the web layer so Firestore works
      await signInWithCredential(auth, credential);
    } catch (e: unknown) {
      console.error('Native Google Sign-In failed', e);
      const errorMessage =
        e instanceof Error ? e.message : 'Google Sign-In failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (providerId: string) => {
    setLoading(true);
    setError(null);
    try {
      // 1. Sign in on the native layer using specialized methods
      let result;
      if (providerId === 'microsoft.com') {
        result = await FirebaseAuthentication.signInWithMicrosoft({
          scopes: ['openid', 'email', 'profile', 'User.Read'],
        });
      } else {
        result = await FirebaseAuthentication.signInWithYahoo({
          scopes: ['openid', 'email', 'profile'],
        });
      }

      if (!result.credential?.idToken && !result.credential?.accessToken) {
        throw new Error(`${providerId} Sign-In was cancelled or failed.`);
      }

      // 2. Create web credentials from the native result
      const provider = new OAuthProvider(providerId);

      let credential;
      if (providerId === 'yahoo.com' && result.credential?.idToken) {
        credential = provider.credential({
          idToken: result.credential.idToken,
          accessToken: result.credential?.accessToken ?? undefined,
          rawNonce: result.credential?.nonce || undefined,
        });
      } else {
        credential = provider.credential({
          idToken: result.credential?.idToken || undefined,
          accessToken: result.credential?.accessToken || undefined,
        });
      }

      // 3. Sign in on the web layer so Firestore works
      await signInWithCredential(auth, credential);
    } catch (e: unknown) {
      console.error(`Native ${providerId} Sign-In failed`, e);
      const errorMessage =
        e instanceof Error ? e.message : `${providerId} Sign-In failed`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLinkSignIn = async () => {
    if (!email) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError(null);

    const actionCodeSettings = {
      // This URL must be whitelisted in the Firebase Console (Authentication > Settings > Authorized Domains)
      url: baseUrl,
      handleCodeInApp: true,
      iOS: { bundleId: packageName },
      android: {
        packageName: packageName,
        installApp: true,
      },
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setLinkSent(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to send email link');
    } finally {
      setLoading(false);
    }
  };

  const navigateToPrivacy = () => {
    window.history.pushState({}, '', '/privacy-policy');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-50 p-6 transition-colors duration-300 dark:bg-black">
      <div className="w-full max-w-md space-y-8 rounded-[40px] border border-zinc-100 bg-white p-8 text-center shadow-2xl dark:border-zinc-800 dark:bg-zinc-900">
        <div className="space-y-4">
          <div className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl">
            <img
              src="/images/logo-dark.webp"
              alt="Kówópé Logo"
              className="h-full w-full object-cover dark:hidden"
            />
            <img
              src="/images/logo-white.webp"
              alt="Kówópé Logo"
              className="hidden h-full w-full object-cover dark:block"
            />
          </div>

          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Kówópé
            </h1>
            <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              Secure native authentication for your mobile device.
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50 p-4 text-left text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-400">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {linkSent && (
          <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-left text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-400">
            <Mail className="h-5 w-5 shrink-0" />
            <p className="text-sm font-medium">
              Check your email! We've sent a login link to {email}.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              <button
                onClick={handleGoogleSignIn}
                className="flex w-full items-center justify-center gap-3 rounded-2xl bg-zinc-900 py-4 font-bold text-white shadow-lg transition-all hover:opacity-90 active:scale-95 dark:bg-zinc-100 dark:text-zinc-900"
              >
                <img
                  src="/images/google-logo.webp"
                  alt="Google"
                  className="h-5 w-5"
                />
                Sign in with Google
              </button>

              {config.showYahooMicrosoft && (
                <>
                  <button
                    onClick={() => handleOAuthSignIn('microsoft.com')}
                    className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#00a4ef] py-4 font-bold text-white shadow-lg transition-all hover:opacity-90 active:scale-95"
                  >
                    <img
                      src="/images/microsoft-logo-white.webp"
                      alt="Microsoft"
                      className="h-5 w-5"
                    />
                    Sign in with Microsoft
                  </button>

                  <button
                    onClick={() => handleOAuthSignIn('yahoo.com')}
                    className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#6001d2] py-4 font-bold text-white shadow-lg transition-all hover:opacity-90 active:scale-95"
                  >
                    <img
                      src="/images/yahoo-logo-white.webp"
                      alt="Yahoo"
                      className="h-5 w-5"
                    />
                    Sign in with Yahoo
                  </button>
                </>
              )}

              {config.showEmailLoginLink && (
                <>
                  <div className="relative py-4">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-zinc-200 dark:border-zinc-800"></span>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-zinc-50 px-2 text-zinc-500 dark:bg-black">
                        Or sign in with email
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-2xl border border-zinc-200 bg-white px-5 py-4 text-sm transition-all outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white"
                    />
                    <button
                      onClick={handleEmailLinkSignIn}
                      className="flex w-full items-center justify-center gap-3 rounded-2xl bg-indigo-600 py-4 font-bold text-white shadow-lg transition-all hover:bg-indigo-700 active:scale-95"
                    >
                      <Mail className="h-5 w-5" />
                      Send Login Link
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="mt-8 border-t border-zinc-100 pt-6 dark:border-zinc-800">
          <p className="text-xs text-zinc-400">
            For other sign-in methods, visit{' '}
            <a
              href={baseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-indigo-600 underline underline-offset-4 hover:text-indigo-500 dark:text-indigo-400"
            >
              {displayUrl}
            </a>
          </p>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-6">
        {baseUrl && (
          <a
            href={baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs font-bold text-zinc-400 transition-colors hover:text-indigo-600"
          >
            <Globe className="h-3.5 w-3.5" />
            Visit our Website
          </a>
        )}

        <button
          onClick={navigateToPrivacy}
          className="flex items-center gap-2 text-xs font-bold text-zinc-400 transition-colors hover:text-indigo-600"
        >
          <Shield className="h-3.5 w-3.5" />
          Privacy Policy
        </button>
      </div>
    </div>
  );
}
